import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'

type Point = { x: number; y: number }

/** De Casteljau: split a cubic bezier at parameter t into two sub-curves. */
export function splitCubicAt(
  p0: Point,
  cp1: Point,
  cp2: Point,
  p3: Point,
  t: number
): { left: [Point, Point, Point, Point]; right: [Point, Point, Point, Point] } {
  const lerp = (a: Point, b: Point, u: number): Point => ({
    x: a.x + (b.x - a.x) * u,
    y: a.y + (b.y - a.y) * u,
  })
  const q0 = lerp(p0, cp1, t)
  const q1 = lerp(cp1, cp2, t)
  const q2 = lerp(cp2, p3, t)
  const r0 = lerp(q0, q1, t)
  const r1 = lerp(q1, q2, t)
  const s = lerp(r0, r1, t)

  return {
    left: [p0, q0, r0, s],
    right: [s, r1, q2, p3],
  }
}

/** Evaluate a cubic bezier at parameter t. */
function evalCubic(p0: Point, cp1: Point, cp2: Point, p3: Point, t: number): Point {
  const mt = 1 - t

  return {
    x: mt * mt * mt * p0.x + 3 * mt * mt * t * cp1.x + 3 * mt * t * t * cp2.x + t * t * t * p3.x,
    y: mt * mt * mt * p0.y + 3 * mt * mt * t * cp1.y + 3 * mt * t * t * cp2.y + t * t * t * p3.y,
  }
}

function distSq(ax: number, ay: number, bx: number, by: number): number {
  return (ax - bx) * (ax - bx) + (ay - by) * (ay - by)
}

/** Find closest point on a cubic bezier, returns { t, dist }. */
function closestPointOnCubic(
  p0: Point,
  cp1: Point,
  cp2: Point,
  p3: Point,
  px: number,
  py: number
): { t: number; dist: number } {
  const SAMPLES = 50
  let bestT = 0
  let bestDsq = Infinity

  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES
    const pt = evalCubic(p0, cp1, cp2, p3, t)
    const dsq = distSq(pt.x, pt.y, px, py)

    if (dsq < bestDsq) {
      bestDsq = dsq
      bestT = t
    }
  }

  // Golden-section refinement
  let lo = Math.max(0, bestT - 1 / SAMPLES)
  let hi = Math.min(1, bestT + 1 / SAMPLES)

  for (let iter = 0; iter < 12; iter++) {
    const mid1 = lo + (hi - lo) / 3
    const mid2 = hi - (hi - lo) / 3
    const pt1 = evalCubic(p0, cp1, cp2, p3, mid1)
    const pt2 = evalCubic(p0, cp1, cp2, p3, mid2)

    if (distSq(pt1.x, pt1.y, px, py) < distSq(pt2.x, pt2.y, px, py)) {
      hi = mid2
    } else {
      lo = mid1
    }
  }

  bestT = (lo + hi) / 2
  const finalPt = evalCubic(p0, cp1, cp2, p3, bestT)

  return { t: bestT, dist: Math.sqrt(distSq(finalPt.x, finalPt.y, px, py)) }
}

/** Find closest point on a line segment [p0→p1], returns { t, dist }. */
function closestPointOnLine(
  p0: Point,
  p1: Point,
  px: number,
  py: number
): { t: number; dist: number } {
  const dx = p1.x - p0.x
  const dy = p1.y - p0.y
  const lenSq = dx * dx + dy * dy

  if (lenSq < 1e-10) {
    return { t: 0, dist: Math.sqrt(distSq(p0.x, p0.y, px, py)) }
  }

  const t = Math.max(0, Math.min(1, ((px - p0.x) * dx + (py - p0.y) * dy) / lenSq))
  const cx = p0.x + t * dx
  const cy = p0.y + t * dy

  return { t, dist: Math.sqrt(distSq(cx, cy, px, py)) }
}

/**
 * Find the closest segment on a path to the given canvas-space point (px, py).
 * Path points are in LOCAL space; ox/oy is the shape offset (shape.x, shape.y).
 * Returns segmentIndex (start anchor index), t on that segment, dist, and canvas-space hit coords.
 */
export function closestPointOnPath(
  points: PenAnchorPoint[],
  isClosed: boolean,
  ox: number,
  oy: number,
  px: number,
  py: number,
  subpaths?: PenSubpathRange[]
): { segmentIndex: number; t: number; dist: number; hitX: number; hitY: number } | null {
  const n = points.length

  if (n < 2) return null

  const ranges: PenSubpathRange[] =
    subpaths && subpaths.length > 0 ? subpaths : [{ start: 0, end: n, isClosed }]
  let best: { segmentIndex: number; t: number; dist: number; hitX: number; hitY: number } | null =
    null

  for (const range of ranges) {
    const rn = range.end - range.start

    if (rn < 2) continue

    const segCount = range.isClosed ? rn : rn - 1

    for (let k = 0; k < segCount; k++) {
      const i = range.start + k
      const a = points[i]
      const b = points[range.start + ((k + 1) % rn)]
      const p0: Point = { x: a.x + ox, y: a.y + oy }
      const p3: Point = { x: b.x + ox, y: b.y + oy }
      const cp1: Point = a.handleOut ? { x: a.handleOut.x + ox, y: a.handleOut.y + oy } : p0
      const cp2: Point = b.handleIn ? { x: b.handleIn.x + ox, y: b.handleIn.y + oy } : p3
      const isCurve = !!(a.handleOut || b.handleIn)
      const res = isCurve
        ? closestPointOnCubic(p0, cp1, cp2, p3, px, py)
        : closestPointOnLine(p0, p3, px, py)

      if (!best || res.dist < best.dist) {
        let hitX: number, hitY: number

        if (isCurve) {
          const hp = evalCubic(p0, cp1, cp2, p3, res.t)

          hitX = hp.x
          hitY = hp.y
        } else {
          hitX = p0.x + res.t * (p3.x - p0.x)
          hitY = p0.y + res.t * (p3.y - p0.y)
        }

        best = { segmentIndex: i, t: res.t, dist: res.dist, hitX, hitY }
      }
    }
  }

  return best
}
